import {
  Button,
  IllustrationHeaderVariant,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
} from '@pluxee-design-system/core';
import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';

interface ModalProviderProps {
  children: ReactNode;
}

export type ModalFormChildFn = (
  modalFormId: string,
  onSubmit: (values: FieldValues) => void,
  onOk: () => void,
  onClose: () => void,
) => ReactNode;

interface CustomModalParams {
  custom: (onOk: () => void, onClose: () => void) => ReactNode;
}

interface BasicModalParams {
  cancelId?: string;
  cancelText?: string;
  confirmId?: string;
  confirmText?: string;
  children?: ReactNode | ModalFormChildFn;
  hideCloseButton?: boolean;
  ignoreClickOutside?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  reverseOrder?: boolean; // to switch actions buttons
  title?: string;
  subTitle?: string;
  size?: 'xxl';
  text?: ReactNode;
  illustration?: {
    variant?: `${IllustrationHeaderVariant}`;
    withoutArrow?: boolean;
    illustration: React.ReactNode;
  };
}

export type ShowModalParams = BasicModalParams | CustomModalParams;

export type ModalContextType = (params: ShowModalParams) => Promise<boolean | FieldValues>;

type ResolverFn = (value: boolean | FieldValues) => void;

const modalFormId = 'modal-form';

const ModalContext = createContext<ModalContextType>(() => Promise.resolve(false));
const isCustom = (modalParams?: ShowModalParams): modalParams is CustomModalParams => {
  if (!modalParams) return false;
  const custom = (modalParams as CustomModalParams)?.custom;
  return custom !== undefined && custom !== null;
};

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modalProps, setModalProps] = useState<ShowModalParams | undefined>(undefined);
  const customHandleOk = isCustom(modalProps) ? undefined : modalProps?.onOk;
  const customHandleCancel = isCustom(modalProps) ? undefined : modalProps?.onCancel;
  const resolver = useRef<ResolverFn>(null);
  const closeModal = useCallback(() => {
    setModalProps(undefined);
  }, [setModalProps]);

  const handleShow = useCallback<ModalContextType>(
    (params: ShowModalParams) => {
      setModalProps(params);

      return new Promise(function (resolve: ResolverFn) {
        resolver.current = resolve;
      });
    },
    [setModalProps],
  );

  const handleOk = useCallback(async () => {
    customHandleOk?.();
    resolver.current?.(true);
    closeModal();
  }, [closeModal, customHandleOk]);

  const handleFormSubmit = useCallback(
    (values: FieldValues) => {
      resolver.current?.(values);
      closeModal();
    },
    [closeModal],
  );

  const handleCancel = useCallback(() => {
    customHandleCancel?.();
    resolver.current?.(false);
    closeModal();
  }, [closeModal, customHandleCancel]);

  return (
    <ModalContext.Provider value={handleShow}>
      {modalProps &&
        (isCustom(modalProps) ? (
          modalProps.custom(handleOk, handleCancel)
        ) : (
          <Modal
            closeButtonVisible={!modalProps.hideCloseButton}
            closeOnEsc={!modalProps.hideCloseButton}
            closeOnOverlayClick={!(modalProps.ignoreClickOutside || modalProps.hideCloseButton)}
            illustrationHeader={modalProps.illustration}
            isOpen
            onClose={handleCancel}
            subTitle={modalProps.subTitle || undefined}
            title={modalProps.title!}
            // consumed by object spread, size would be undefined if size={modalProps.size = undefined}
            {...(modalProps.size ? { size: modalProps.size } : {})}
          >
            <ModalBody>
              {modalProps.text && (
                <Text variant="body.largeMedium" color="semantic.text.tertiary">
                  {modalProps.text}
                </Text>
              )}
              {'function' === typeof modalProps.children
                ? modalProps.children(modalFormId, handleFormSubmit, handleOk, handleCancel)
                : modalProps.children}
            </ModalBody>
            {(modalProps.cancelText || modalProps.confirmText) && (
              <ModalFooter>
                {modalProps.cancelText && (
                  <Button
                    id={modalProps.cancelId}
                    onClick={modalProps.reverseOrder ? handleOk : handleCancel}
                    variant="primaryOutlined"
                  >
                    {modalProps.cancelText}
                  </Button>
                )}
                {modalProps.confirmText && (
                  <Button
                    {...('function' === typeof modalProps.children
                      ? { form: modalFormId, type: 'submit' }
                      : { onClick: modalProps.reverseOrder ? handleCancel : handleOk })}
                    id={modalProps.confirmId}
                    variant="primaryFilled"
                  >
                    {modalProps.confirmText}
                  </Button>
                )}
              </ModalFooter>
            )}
          </Modal>
        ))}
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
};

export default useModal;
